import dayjs from 'dayjs'
import { isNil, isNumber } from 'lodash'
import { TaskFormData } from 'components/TasksV2/TaskCreation/FormStepContext'
import {
  calculateDaysOfWeek,
  calculateRecurrenceDeadline,
  calculateRecurrenceFrequency,
  calculateTots,
} from 'components/TasksV2/TaskCreation/utils'
import { ICreateJob } from 'redux/actions/admin'
import { SERVICE_QUEUE_MAPPING } from 'components/AdminConsole/Jobs/utils'
import { getUTCOffset } from 'utils'
import keys from 'constants/keys'
import { resolveAllowedOverdueDays } from 'components/TasksV2/TaskCreation/model/taskSubmission'

export const OFFSET_HALFWAY = -2
export const OFFSET_WHEN_DONE = -1

export type AiTaskDraft = Partial<Omit<TaskFormData, 'TASK_SETTINGS'>> & {
  TASK_SETTINGS?: TaskFormData['TASK_SETTINGS'] & {
    ai_schedule?: { offsetMinutes: number | null }
    send_action_pop?: boolean
  }
  AI_PROMPT?: { prompt: string; reference_images?: string[] }
}

/** The AI contract intentionally differs from ordinary/VM task publishing. */
export function buildAiTaskPayload(allData: AiTaskDraft, promptId: string) {
  const { title = '', sub_tasks = [] } = allData.SUBTASK_TASK_CREATION || {}
  const settings = (allData.TASK_SETTINGS || {}) as TaskFormData['TASK_SETTINGS']
  const { groups, users, tags } = settings?.audience || { groups: [], users: [], tags: [] }
  const {
    from_ts = settings?.from_ts,
    deadline = settings?.deadline,
    frequency_time = '',
    monthly_range = [],
    is_recurring = false,
    days_of_week = [],
    allowed_overdue_days: timeFrameOverdueDays,
    recurrence_frequency = null,
  } = allData.TIME_FRAME_SELECTION || {}
  const formattedFromTs = dayjs(from_ts ?? new Date()).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  const formattedDeadline = dayjs(deadline ?? new Date()).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  const recurrence_deadline = calculateRecurrenceDeadline({
    is_recurring,
    recurrence_frequency,
    from_ts: formattedFromTs,
    frequency_time,
    monthly_range,
    days_of_week,
  })
  const subtasksWithSettings = sub_tasks.map((subtask, index) => {
    const isQuestion = subtask.type === keys.SUBTASK_TYPES.OPEN_QUESTION
    return {
      ...subtask,
      order_index: index,
      ...(!isQuestion ? { is_ai_enhanced: true, disable_gallery_upload: settings?.disable_gallery_upload } : {}),
      ...(isQuestion ? { content: { rich_text: null, images: [] }, is_numeric: false } : {}),
      prompt_id: keys.AI_IMAGE_SUBTASK.CONST_PROMPT_ID,
      message_id: promptId,
      reference_images: allData.AI_PROMPT?.reference_images || [],
    }
  })
  return {
    from_ts: formattedFromTs,
    to_ts: calculateTots({ is_recurring, deadline: deadline as string | null, formattedDeadline }),
    deadline: formattedDeadline.toString(),
    recurrence_deadline,
    groups,
    users,
    tags,
    utc_offset: getUTCOffset(),
    send_schedule_push: settings?.send_schedule_push,
    send_campaign_start_push: true,
    data: {
      title,
      is_recurring,
      recurrence_frequency: calculateRecurrenceFrequency({ frequency: recurrence_frequency, days_of_week }),
      recurrence_interval: is_recurring ? 1 : null,
      recurrence_days_of_week: calculateDaysOfWeek({ recurrence_frequency, days_of_week }),
      recurrence_start_time: is_recurring ? formattedFromTs.slice(8) : null,
      view: keys.DEFAULT_TASK_VIEWS.AI_TASKS,
      categories: [],
      type: settings?.type,
      is_brief_task: true,
      is_individual_results: settings?.is_individual_results,
      allowed_overdue_days: resolveAllowedOverdueDays(timeFrameOverdueDays, settings?.allowed_overdue_days),
      priority: settings?.priority,
      ...(!isNil(settings?.rework_limit) ? { rework_limit: Number(settings?.rework_limit) } : {}),
      allow_completion_after_deadline: settings?.allow_completion_after_deadline,
      watchers: settings?.watchers || [],
      auditors: settings?.auditors || [],
    },
    sub_tasks: subtasksWithSettings,
  }
}

export function computeBriefingFireTs(fromTs: string, deadline: string, offsetMinutes: number | null): string {
  const base = dayjs(fromTs)
  const end = dayjs(deadline)
  if (offsetMinutes === OFFSET_HALFWAY) {
    return base
      .add(Math.floor(end.diff(base, 'minute') / 2), 'minute')
      .second(0)
      .format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  }
  if (offsetMinutes === OFFSET_WHEN_DONE) return end.second(0).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  if (isNumber(offsetMinutes) && offsetMinutes > 0) {
    return base.add(offsetMinutes, 'minute').second(0).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  }
  return base.second(0).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
}

export function buildPopJob({
  popId,
  tid,
  rid,
  title,
  scheduleStartTs,
}: {
  popId: string
  tid: string
  rid: string
  title: string
  scheduleStartTs: string
}): ICreateJob {
  return {
    is_recurring: false,
    job_data: {
      title,
      content: { text: '' },
      action_service: keys.JOBS_SERVICES.POPS,
      action_queue_name: SERVICE_QUEUE_MAPPING[keys.JOBS_SERVICES.POPS],
      action_object_id: tid,
      action_params: { pop_id: popId, rid },
      initiator: 1,
      rid,
      recurrence_frequency: null,
      recurrence_interval: 1,
      recurrence_days_of_week: [],
      schedule_start_ts: scheduleStartTs,
      schedule_end_ts: null,
    },
  }
}
